import type React from 'react';
import { type JSX, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { ParseArticleContent, ParseHtmlDefault } from 'utils/miscUtils/rawHtml';
import { scrubContent } from 'utils/stringUtils';
import type { Article, ArticlePreview } from 'interfaces/content/articles/Articles';
import ContentWithEmbed from 'components/ContentWithEmbed';
import AdminBar from 'components/AdminBar';
import TableOfContents from 'components/TableOfContents';
import { UserPaywallContext } from 'providers/PaywallProvider';
import ContentWithRVWidgets from 'components/ContentWithRVWidgets';
import { RedVentureWidgets } from 'constants/constants';
import { UserContext } from 'providers/UserProvider';
import ArticleSeriesList from 'components/ArticleSeries/ArticleSeriesList';
import StickyCTA from 'components/StickyCTA';
import { getArticleTrackingData } from 'utils/trackDataUtils';
import useTrackArticleClickEvents from 'hooks/useTrackArticleClickEvents';
import { useHeaderHeight } from 'hooks/useHeaderHeight';
import AuthorBio from 'components/AuthorBio';
import Heading from 'styles/typography/Heading';
import { handleReadMoreToggle, toggleReadMoreBoxes } from 'utils/miscUtils/toggleReadMoreBoxes';
import type { Author } from 'interfaces/content/Authors';
import ScrollUpButton from 'components/Globals/Base/ScrollUpButton';
import { LANDING_PAGE_TEST_IDS } from 'constants/testsIds/landingPage';
import useRedVentureIframeObserver from 'hooks/useRedVentureIframesObserver';
import ErrorBoundary from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import ArticleSection from 'components/recommends/ArticleSection';
import { SectionVariant } from 'interfaces/Section';
import AccoladesHeader from './AccoladesHeader';
import * as S from './LandingPageStyles';
import LandingPageHeader from './LandingPageHeader';

export interface LandingPageProps {
  article: Article;
  mostPopular?: ArticlePreview[];
  subdomain: string;
  embeds: { node: string; index: number; component: JSX.Element }[];
  zipCode?: string;
  isAwardsPage?: boolean;
  widgets?: { [key: string]: string };
  adFrequency?: number;
  hasRedVentureWidgets?: boolean;
}

const LandingPage: React.FC<LandingPageProps> = ({
  article,
  mostPopular,
  subdomain,
  embeds,
  zipCode,
  isAwardsPage,
  widgets,
  adFrequency,
  hasRedVentureWidgets,
}) => {
  const {
    authors,
    content,
    databaseId,
    floatingNavContent,
    floatingNavTitle,
    title,
    reviewedBy,
    series,
    id,
    imageCaption,
    postSettings: { mediaItem },
  } = article;

  const cleanedContent = scrubContent(content);

  const topWidgetsSection = widgets?.['top-widgets-section'];
  const keyTakeawaysWidget = widgets?.['key-takeaways-widget'];
  const stickyCtaWidget = widgets?.['sticky-cta-widget'];

  const widgetsInContent = useMemo(
    () => Object.values(RedVentureWidgets).filter((widgetId) => content.indexOf(widgetId) > -1),
    [content],
  );

  const { headerHeight } = useHeaderHeight(!!floatingNavContent);

  const { paywallCheck } = useContext(UserPaywallContext).paywallState;
  const { userState: user } = useContext(UserContext);

  const RawHtmlClass = paywallCheck === 'no-paywall' ? 'rawHtml-content' : 'rawHtml-content-no-nativo';

  const { trackingData } = useMemo(() => getArticleTrackingData(article, user, subdomain), [article, subdomain, user]);
  useTrackArticleClickEvents(trackingData, subdomain);

  const handleReadMore = useCallback((event: MouseEvent) => {
    handleReadMoreToggle(event);
  }, []);

  const contentRef = useRef<HTMLDivElement>(null);
  useRedVentureIframeObserver(contentRef, article.postId, hasRedVentureWidgets);

  useEffect(() => {
    toggleReadMoreBoxes(handleReadMore);
  }, [handleReadMore, adFrequency]);

  return (
    <div id='content'>
      <AdminBar pageId={databaseId} />
      <S.LandingPageWrapper
        $headerHeight={headerHeight}
        $hasTOC={!!floatingNavContent}
      >
        <S.TopSection
          className='top-section'
          $hasTOC={!!floatingNavContent}
        >
          {isAwardsPage ? (
            <AccoladesHeader
              title={title}
              hasTOC={!!floatingNavContent}
              image={{
                altText: mediaItem.altText || imageCaption,
                mediaItemUrl: mediaItem.mediaItemUrl,
              }}
            />
          ) : (
            <LandingPageHeader
              article={article}
              trackingData={trackingData}
            />
          )}
          {topWidgetsSection && (
            <S.ArticleContent
              className='top-content'
              $headerHeight={headerHeight}
              data-cy={LANDING_PAGE_TEST_IDS.TOP_CONTENT}
            >
              {ParseArticleContent(topWidgetsSection, false)}
            </S.ArticleContent>
          )}
        </S.TopSection>
        {floatingNavContent && (
          <TableOfContents
            title={floatingNavTitle || 'Contents'}
            contents={floatingNavContent}
            headerHeight={headerHeight}
            className='toc'
          />
        )}
        <S.ArticleContent
          className='article-content'
          $headerHeight={headerHeight}
          $hasTOC={!!floatingNavContent}
          data-cy={LANDING_PAGE_TEST_IDS.CONTENT}
        >
          <ContentWithRVWidgets
            widgetIds={widgetsInContent}
            microsite={subdomain}
            zipCode={zipCode}
            trackingData={trackingData}
            postId={article.postId}
          >
            <>
              <ErrorBoundary fragment={UIFragments.KEYTAKEAWAYS_WIDGET}>
                {keyTakeawaysWidget && ParseHtmlDefault(keyTakeawaysWidget)}
              </ErrorBoundary>
              <ContentWithEmbed
                embedInstructions={embeds.map((embed) => ({ index: embed.index, node: embed.node }))}
                adFrequency={0}
                section={article.primarySection.name}
                articleUrl={article.url}
              >
                {[
                  ...embeds.map((embed) => embed.component),
                  <div
                    key='article-content'
                    data-cy='articleContent'
                    className={RawHtmlClass}
                    ref={contentRef}
                  >
                    {ParseArticleContent(cleanedContent, true)}
                  </div>,
                ]}
              </ContentWithEmbed>
            </>
          </ContentWithRVWidgets>
        </S.ArticleContent>
        <S.BottomSection className='bottom-section'>
          {authors && authors.length > 0 && (
            <S.AuthorsBioCards data-cy={LANDING_PAGE_TEST_IDS.AUTHORS_BIO_CARDS}>
              <Heading
                as='h4'
                $fontFamily='graphikCond'
                $size={{ default: 'level3' }}
                data-cy={LANDING_PAGE_TEST_IDS.AUTHORS_BIO_SECTION_TITLE}
              >
                About the contributors
              </Heading>
              {authors.map((author) => (
                <AuthorBio
                  key={author.email}
                  className='author-bio'
                  author={author}
                />
              ))}
              {reviewedBy && (
                <AuthorBio
                  key={reviewedBy.uri}
                  className='author-bio'
                  author={reviewedBy as Author}
                />
              )}
            </S.AuthorsBioCards>
          )}
          <S.LandingPageSocialContainer data-cy={LANDING_PAGE_TEST_IDS.SOCIAL_CONTAINER}>
            <S.LandingPageSocialShare title={title} />
          </S.LandingPageSocialContainer>
          {series.map((seriesList, idx) => {
            const displaySeries = seriesList.name && seriesList?.posts?.length > 0;
            if (!displaySeries) return null;
            return (
              <ArticleSeriesList
                seriesID={`articleSeries${idx}`}
                key={seriesList.name}
                series={seriesList}
                currentArticleId={id}
                dataCy={LANDING_PAGE_TEST_IDS.SERIES_LIST}
              />
            );
          })}
          {mostPopular && mostPopular.length > 0 && (
            <ArticleSection
              title='Top Articles'
              trackingData={trackingData}
              variant={SectionVariant.TopArticles2}
              articles={mostPopular}
            />
          )}
        </S.BottomSection>
      </S.LandingPageWrapper>
      <ScrollUpButton
        dataCy={LANDING_PAGE_TEST_IDS.SCROLL_UP}
        isRecommends
      />
      {!!stickyCtaWidget && (
        <StickyCTA dataCy={LANDING_PAGE_TEST_IDS.STICKY_CTA}>{ParseHtmlDefault(stickyCtaWidget)}</StickyCTA>
      )}
    </div>
  );
};

export { LandingPage };
